import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Image as ImageIcon, Loader2, Clock, ArrowRight, HardDrive, Trash2,
  Folder, FolderOpen, ChevronLeft, ChevronRight, ChevronDown, Search, Cloud,
  Download, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────── Drive folder browser ──────────────────────────────────── */

// Flat-render helper: walks the tree into a list of { depth, node } rows.
// Doing this instead of a recursive component avoids babel-plugin stack issues.
function flattenTree(root, openIds) {
  const out = [];
  const walk = (node, depth) => {
    out.push({ node, depth });
    if (openIds.has(node.id)) {
      (node.subfolders || []).forEach((c) => walk(c, depth + 1));
    }
  };
  walk(root, 0);
  return out;
}

function FolderTreeView({ tree }) {
  // Auto-expand the root node
  const [openIds, setOpenIds] = useState(() => new Set([tree.id]));
  const rows = flattenTree(tree, openIds);
  return (
    <div>
      {rows.map(({ node, depth }) => {
        const hasChildren = (node.subfolders || []).length > 0;
        const isOpen = openIds.has(node.id);
        return (
          <div
            key={node.id}
            className="flex items-center gap-1.5 py-0.5 hover:bg-[#1A1A1D] rounded-sm cursor-pointer"
            style={{ paddingLeft: 8 + depth * 12 }}
            onClick={() => {
              if (!hasChildren) return;
              setOpenIds((prev) => {
                const next = new Set(prev);
                if (next.has(node.id)) next.delete(node.id); else next.add(node.id);
                return next;
              });
            }}
            data-testid={`tree-node-${node.id}`}
          >
            {hasChildren ? (
              isOpen ? <ChevronDown className="h-3 w-3 text-[#71717A] shrink-0" />
                     : <ChevronRight className="h-3 w-3 text-[#71717A] shrink-0" />
            ) : (
              <span className="w-3 shrink-0" />
            )}
            <Folder className="h-3.5 w-3.5 text-[#F59E0B] shrink-0" />
            <span className="text-[11px] text-[#F8F9FA] truncate flex-1">{node.name}</span>
            <span className="text-[10px] text-[#71717A] font-mono shrink-0">
              {node.image_count} img
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DriveBrowser({ onImported }) {
  const [linkInput, setLinkInput] = useState("");
  const [history, setHistory] = useState([]); // [{ id, name }]
  const [listing, setListing] = useState(null); // non-recursive: { folder, subfolders[], images[] }
  const [tree, setTree] = useState(null);       // recursive: { folder, tree, images[], folder_count }
  const [recursive, setRecursive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  const loadFolder = async (folderRef, push = true) => {
    if (!folderRef) return;
    setLoading(true);
    setError(null);
    setTree(null);  // reset tree when navigating
    try {
      const { data } = await axios.get(`${API}/drive/list`, {
        params: { folder: folderRef, recursive: false },
      });
      setListing(data);
      if (push) {
        setHistory((h) => [...h, { id: data.folder.id, name: data.folder.name }]);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not load that folder";
      setError(msg);
      setListing(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRecursive = async () => {
    if (!listing) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/drive/list`, {
        params: { folder: listing.folder.id, recursive: true },
        timeout: 60000,
      });
      setTree(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not scan sub-folders");
    } finally {
      setLoading(false);
    }
  };

  // If user toggles recursive on with a listing loaded, fetch the tree
  useEffect(() => {
    if (recursive && listing && !tree) {
      loadRecursive();
    }
  }, [recursive, listing, tree]); // loadRecursive is stable enough for our use

  const handleResolve = (e) => {
    e?.preventDefault?.();
    if (!linkInput.trim()) return;
    setHistory([]);
    loadFolder(linkInput.trim(), true);
  };

  const goBack = () => {
    if (history.length <= 1) return;
    const next = history.slice(0, -1);
    setHistory(next.slice(0, -1));
    loadFolder(next[next.length - 1].id, true);
  };

  const goTo = (idx) => {
    const target = history[idx];
    if (!target) return;
    setHistory(history.slice(0, idx));
    loadFolder(target.id, true);
  };

  const importFolder = async () => {
    if (!listing) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/drive/import`, {
        folder: listing.folder.id,
        recursive,
      });
      toast.success(`Importing ${data.photo_count} photo(s) from Drive…`);
      onImported(data.session_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Drive import failed");
    } finally {
      setImporting(false);
    }
  };

  const totalImageCount = recursive
    ? (tree?.images?.length ?? 0)
    : (listing?.images?.length ?? 0);

  return (
    <div className="space-y-4" data-testid="drive-browser">
      {/* Link input */}
      <form onSubmit={handleResolve} className="flex gap-2" data-testid="drive-link-form">
        <div className="relative flex-1">
          <Cloud className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            placeholder="Paste a Google Drive folder link…"
            className="w-full pl-9 pr-3 h-11 bg-[#121214] border border-[#27272A] rounded-sm text-[#F8F9FA] placeholder:text-[#3F3F46] focus:border-[#F8F9FA] focus:outline-none transition-colors text-sm"
            data-testid="drive-link-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !linkInput.trim()}
          className="bg-white text-black hover:bg-gray-200 h-11 rounded-sm font-medium px-5"
          data-testid="drive-link-resolve"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" />Browse</>}
        </Button>
      </form>

      <p className="text-[11px] text-[#71717A]">
        The folder must be shared as <span className="text-[#F8F9FA]">&ldquo;Anyone with the link can view&rdquo;</span>.
        Click into sub-folders to find the one with your photos, or turn on
        <span className="text-[#F8F9FA]"> Include sub-folders</span> to pull every image at once.
      </p>

      {error && (
        <div className="p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm flex gap-2" data-testid="drive-error">
          <AlertTriangle className="h-4 w-4 text-[#EF4444] shrink-0 mt-0.5" />
          <p className="text-[12px] text-[#FCA5A5]">{error}</p>
        </div>
      )}

      {listing && !error && (
        <div className="bg-[#121214] border border-[#27272A] rounded-sm" data-testid="drive-listing">
          {/* Breadcrumb + recursive toggle */}
          <div className="flex items-center gap-2 p-2 border-b border-[#27272A]">
            <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
              <Button
                variant="ghost" size="sm"
                onClick={goBack}
                disabled={history.length <= 1}
                className="h-7 w-7 p-0 text-[#71717A] hover:text-white hover:bg-[#1A1A1D] rounded-sm shrink-0"
                data-testid="drive-back-button"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <div className="flex items-center gap-1 text-[11px]">
                {history.map((h, i) => (
                  <span key={`${h.id}-${i}`} className="flex items-center gap-1 shrink-0">
                    {i > 0 && <span className="text-[#3F3F46]">/</span>}
                    <button
                      onClick={() => goTo(i + 1)}
                      className={`hover:text-white truncate max-w-[160px] ${
                        i === history.length - 1 ? "text-[#F8F9FA]" : "text-[#71717A]"
                      }`}
                      title={h.name}
                    >
                      {h.name}
                    </button>
                  </span>
                ))}
              </div>
            </div>
            {/* Include sub-folders toggle */}
            <label
              className="flex items-center gap-1.5 text-[11px] text-[#A1A1AA] cursor-pointer shrink-0"
              data-testid="recursive-toggle-label"
            >
              <input
                type="checkbox"
                checked={recursive}
                onChange={(e) => setRecursive(e.target.checked)}
                className="accent-[#F8F9FA]"
                data-testid="recursive-toggle"
              />
              Include sub-folders
            </label>
          </div>

          {/* Contents: either flat (immediate) or tree (recursive) */}
          <div className="max-h-[42vh] overflow-y-auto p-2">
            {recursive ? (
              tree ? (
                <FolderTreeView tree={tree.tree} />
              ) : (
                <div className="flex items-center gap-2 p-3 text-[11px] text-[#71717A]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Scanning sub-folders…
                </div>
              )
            ) : (
              <>
                {listing.subfolders.length === 0 && listing.images.length === 0 && (
                  <p className="text-[11px] text-[#71717A] p-3 text-center">
                    This folder is empty.
                  </p>
                )}

                {/* Subfolders */}
                {listing.subfolders.map((sf) => (
                  <button
                    key={sf.id}
                    onClick={() => loadFolder(sf.id, true)}
                    className="w-full flex items-center gap-2 p-2 hover:bg-[#1A1A1D] rounded-sm text-left transition-colors"
                    data-testid={`drive-folder-${sf.id}`}
                  >
                    <Folder className="h-4 w-4 text-[#F59E0B] shrink-0" />
                    <span className="text-[12px] text-[#F8F9FA] truncate flex-1">{sf.name}</span>
                    <ArrowRight className="h-3 w-3 text-[#3F3F46] shrink-0" />
                  </button>
                ))}

                {/* Image files */}
                {listing.images.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                    {listing.images.slice(0, 24).map((img) => (
                      <div
                        key={img.id}
                        className="aspect-square bg-[#0A0A0C] border border-[#27272A] rounded-sm flex flex-col items-center justify-center p-1 text-center"
                        data-testid={`drive-image-${img.id}`}
                        title={img.name}
                      >
                        <ImageIcon className="h-4 w-4 text-[#71717A] mb-1" />
                        <span className="text-[8px] text-[#A1A1AA] font-mono uppercase">{img.ext.replace(".", "")}</span>
                        <span className="text-[8px] text-[#3F3F46] truncate w-full px-1">{img.name}</span>
                      </div>
                    ))}
                    {listing.images.length > 24 && (
                      <div className="aspect-square bg-[#0A0A0C] border border-[#27272A] rounded-sm flex items-center justify-center">
                        <span className="text-[10px] text-[#71717A]">+{listing.images.length - 24}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer + Import */}
          <div className="flex items-center justify-between gap-3 p-3 border-t border-[#27272A]">
            <div className="text-[10px] text-[#71717A] font-mono">
              {recursive ? (
                tree ? (
                  <>
                    {tree.folder_count} folder(s) ·{" "}
                    <span className="text-[#F8F9FA]">{tree.images.length} image(s)</span>
                    {tree.unsupported_count > 0 && (
                      <span className="ml-2 text-[#F59E0B]">
                        · {tree.unsupported_count} unsupported
                      </span>
                    )}
                  </>
                ) : "Scanning…"
              ) : (
                <>
                  {listing.subfolders.length} folder(s) ·{" "}
                  <span className="text-[#F8F9FA]">{listing.images.length} image(s)</span>
                  {listing.unsupported_count > 0 && (
                    <span className="ml-2 text-[#F59E0B]">
                      · {listing.unsupported_count} unsupported
                    </span>
                  )}
                </>
              )}
            </div>
            <Button
              onClick={importFolder}
              disabled={importing || totalImageCount === 0 || (recursive && !tree)}
              className="bg-white text-black hover:bg-gray-200 h-8 rounded-sm font-medium text-xs px-4"
              data-testid="drive-import-button"
            >
              {importing ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Importing…</>
              ) : (
                <><Download className="h-3.5 w-3.5 mr-1.5" />
                Import {recursive ? "all sub-folders" : "this folder"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Page ──────────────────────────────────────────────────── */

export default function UploadPage() {
  const [sessions, setSessions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/sessions`).then(r => setSessions(r.data)).catch(() => {});
  }, []);

  // ── Storage panel ────────────────────────────────────────────────
  const [storage, setStorage] = useState(null);
  const [showStorage, setShowStorage] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const refreshStorage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/storage`);
      setStorage(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refreshStorage(); }, [refreshStorage]);

  const fmt = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let v = bytes;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
  };

  const deleteOne = async (sid) => {
    if (!window.confirm("Delete this session and its photos?")) return;
    try {
      const { data } = await axios.delete(`${API}/sessions/${sid}`);
      toast.success(`Freed ${fmt(data.freed_bytes)}`);
      setSessions((cur) => cur.filter((s) => s.id !== sid));
      refreshStorage();
    } catch {
      toast.error("Failed to delete session");
    }
  };

  const bulkCleanup = async (keep) => {
    if (!window.confirm(
      keep === 0
        ? "Delete ALL sessions and free the disk?"
        : `Keep the ${keep} most recent and delete the rest?`
    )) return;
    setCleaning(true);
    try {
      const { data } = await axios.post(`${API}/storage/cleanup?keep=${keep}`);
      toast.success(`Deleted ${data.deleted_count} session(s), freed ${fmt(data.freed_bytes)}`);
      const { data: fresh } = await axios.get(`${API}/sessions`);
      setSessions(fresh);
      refreshStorage();
    } catch {
      toast.error("Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-56px)] flex flex-col items-center px-6 py-12" data-testid="upload-page">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-left">
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-[#F8F9FA] tracking-tight mb-3">
            Group Photo Analyzer
          </h1>
          <p className="text-[#A1A1AA] text-base leading-relaxed">
            Import a Google Drive folder of group photos to detect blinks, analyze
            sharpness, and find the best shot.
          </p>
        </div>

        <DriveBrowser onImported={(sid) => navigate(`/session/${sid}`)} />

        {/* Storage panel + Recent sessions */}
        {sessions.length > 0 && (
          <div className="mt-12 space-y-3" data-testid="recent-sessions">
            <div className="flex items-center justify-between">
              <h2 className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#71717A]">
                Recent
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowStorage(v => !v); refreshStorage(); }}
                className="h-7 text-[10px] text-[#71717A] hover:text-white hover:bg-[#1A1A1D] rounded-sm"
                data-testid="toggle-storage-button"
              >
                <HardDrive className="h-3 w-3 mr-1.5" />
                Storage
              </Button>
            </div>

            {showStorage && storage && (
              <div className="p-4 bg-[#121214] border border-[#27272A] rounded-sm space-y-3" data-testid="storage-panel">
                <div>
                  <div className="flex justify-between text-[11px] mb-1.5 text-[#A1A1AA]">
                    <span>App uploads: {fmt(storage.app_uploads_bytes)}</span>
                    <span>
                      Disk: {fmt(storage.disk.used_bytes)} used / {fmt(storage.disk.total_bytes)} total
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#1A1A1D] rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        storage.disk.free_pct < 10 ? "bg-[#EF4444]" : storage.disk.free_pct < 25 ? "bg-[#F59E0B]" : "bg-[#10B981]"
                      }`}
                      style={{ width: `${Math.min(100, 100 - storage.disk.free_pct)}%` }}
                    />
                  </div>
                  {storage.disk.free_pct < 10 && (
                    <p className="text-[10px] text-[#EF4444] mt-1.5" data-testid="disk-warning">
                      Low disk space ({storage.disk.free_pct}% free) — free up space to import more photos.
                    </p>
                  )}
                </div>

                {/* Bulk actions */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm" variant="outline"
                    disabled={cleaning || storage.sessions.length <= 3}
                    onClick={() => bulkCleanup(3)}
                    className="h-7 border-[#27272A] text-[#F8F9FA] hover:bg-[#1A1A1D] rounded-sm text-[11px]"
                    data-testid="cleanup-keep-3-button"
                  >
                    Keep last 3, delete rest
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    disabled={cleaning || storage.sessions.length <= 1}
                    onClick={() => bulkCleanup(1)}
                    className="h-7 border-[#27272A] text-[#F8F9FA] hover:bg-[#1A1A1D] rounded-sm text-[11px]"
                    data-testid="cleanup-keep-1-button"
                  >
                    Keep only latest
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    disabled={cleaning || storage.sessions.length === 0}
                    onClick={() => bulkCleanup(0)}
                    className="h-7 border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-sm text-[11px]"
                    data-testid="cleanup-all-button"
                  >
                    {cleaning ? <Loader2 className="h-3 w-3 animate-spin" /> : "Delete all"}
                  </Button>
                </div>

                {/* Per-session size list */}
                {storage.sessions.length > 0 && (
                  <div className="max-h-56 overflow-y-auto border-t border-[#27272A] pt-2">
                    {storage.sessions.map((s) => (
                      <div
                        key={s.session_id}
                        className="flex items-center justify-between py-1.5 text-[11px]"
                        data-testid={`storage-row-${s.session_id}`}
                      >
                        <div className="flex-1 truncate text-[#A1A1AA]">
                          <span className="text-[#F8F9FA]">{s.name}</span>
                          <span className="text-[#3F3F46] ml-2">
                            {s.photo_count} photo(s) · {fmt(s.size_bytes)}
                          </span>
                        </div>
                        <button
                          onClick={() => deleteOne(s.session_id)}
                          className="ml-2 text-[#71717A] hover:text-[#EF4444] transition-colors"
                          title="Delete this session"
                          data-testid={`delete-session-${s.session_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              {sessions.slice(0, 5).map((s) => (
                <button
                  key={s.id}
                  onClick={() => navigate(`/session/${s.id}`)}
                  className="w-full flex items-center justify-between p-3 bg-[#121214] border border-[#27272A] rounded-sm hover:bg-[#1A1A1D] hover:border-[#3F3F46] transition-colors text-left"
                  data-testid={`session-${s.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FolderOpen className="h-4 w-4 text-[#71717A]" />
                    <div>
                      <p className="text-sm text-[#F8F9FA]">{s.name}</p>
                      <p className="text-xs text-[#71717A]">
                        {s.photo_count} photos &middot; {s.group_count} groups
                      </p>
                    </div>
                  </div>
                  <Clock className="h-3.5 w-3.5 text-[#71717A]" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
